/**************************************************************************************************/
/*   File: /src/app/shared/components/fileuploader/upload-body/upload-body.component.ts           */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Friday February 22nd 2019                                                      */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Tue May 14 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import { NGXLogger } from 'ngx-logger';

import {
    ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output,
    SimpleChanges
} from '@angular/core';

import * as fromEnums from '../../../enums';
import * as fromModels from '../../../models';

/**
 * The upload body component
 */
@Component({
  selector: 'nwpl-frnt-upload-body',
  templateUrl: './upload-body.component.html',
  styleUrls: ['./upload-body.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UploadBodyComponent implements OnInit, OnChanges {

  /**
   * The context
   */
  @Input() context: string;

  /**
   * The text
   */
  @Input() text: string;

  /**
    * The accepted file array
    */
  @Input() acceptedTypes: Array<string>;

  /**
   * The is file over zone
   */
  @Input() isFileOverZone: boolean;

  /**
   * The max number of file
   */
  @Input() maxFileNumber: number;

  /**
   * The number of files that current stored in the store for limit
   */
  @Input() currentFileCount: number;

  /**
   * The fileChoosed event emitted
   */
  @Output() fileChoosed: EventEmitter<Array<fromModels.FileModel>> = new EventEmitter();

  /**
   * The file over zone
   */
  @Output() fileOverZone: EventEmitter<Event> = new EventEmitter();

  /**
   * The file leave zone
   */
  @Output() fileLeaveZone: EventEmitter<Event> = new EventEmitter();

  /**
   * The file leave zone
   */
  @Output() fileDroppedZone: EventEmitter<Array<fromModels.FileModel>> = new EventEmitter();

  /**
   * The error occurred event emitter
   */
  @Output() errorOccurred: EventEmitter<fromModels.Message> = new EventEmitter();

  /**
   * The constructor
   */
  constructor(private logger: NGXLogger) {
  }


  /**
   * The ng on init hook
   */
  ngOnInit() {
    this.logger.trace('Initialize file upload field component', this.acceptedTypes, this.maxFileNumber, this.currentFileCount);
  }

  /**
  * The ng on init hook
  */
  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.currentFileCount) {
      this.logger.trace('File count is', changes.currentFileCount.currentValue);
    }
  }

  /**
   * Drag the file/img above the drag zone.
   * This function will execute untill the file above the drag zone.
   *
   * @param {Event} event
   * @memberof UploadBodyComponent
   */
  onFileOverZone(event: Event) {

    if (this.isFileOverZone) {
      this.logger.trace('No need to modify the state that file over the zone.');
      return;
    }
    this.fileOverZone.emit(event);
  }

  /**
   *
   * On file leaved
   * @param {*} event
   * @memberof UploadBodyComponent
   */
  onFileLeaveZone(event: any) {
    if (!this.isFileOverZone) {
      return;
    }
    this.fileLeaveZone.emit(event);
  }

  /**
   * When you release the file/img over the drag zone.
   *
   * @param {Event} event
   * @memberof UploadBodyComponent
   */
  onDropFile(fileList: FileList) {
    const files = this.createFileFromFileList(fileList);
    this.logger.trace(files);

    this.fileDroppedZone.emit(files);
    this.fileCountLimitValidation(this.currentFileCount)(files);
  }


  /**
   * Changed file from file chooser
   * @param {FileList} fileList
   * @returns
   * @memberof UploadBodyComponent
   */
  onChangeFilesFromInput(fileList: FileList) {
    this.logger.trace('Changed file list');

    const files = this.createFileFromFileList(fileList);
    this.fileCountLimitValidation(this.currentFileCount)(files);
  }

  /**
   * Pass this validation step only if the number of files that current in the store are
   * smaller then 10.
   *
   * @private
   * @param {any} count
   * @returns
   * @memberof UploadBodyComponent
   */
  private fileCountLimitValidation(count: number) {
    this.logger.trace('Current there %s files in the store.. ', count);

    return (files) => {
      if (count + files.length > this.maxFileNumber) {

        // TO TEST
        this.errorOccurred.emit(
          new fromModels.Message(
            fromEnums.MessageType.WARNING,
            fromEnums.Messages.MAX_FILE_UPLOAD_REACHED,
            fromEnums.MessagePositionEnum.TOP,
            this.context
          )
        );
        return;
      }
      this.fileChoosed.emit(files);
    };
  }


  /**
   * Create new file from file list
   * @private
   * @param {FileList} fileList
   * @returns {FileModel[]}
   */
  private createFileFromFileList(fileList: FileList): any[] {
    const immutateFileList = Array.from(fileList).concat([]),
      tempFileArray = [];
    let fileModels: Array<fromModels.FileModel> = [];

    fileModels = immutateFileList.map(file => {
      if (this.isValidType(file.type)) {
        this.logger.trace('Is valid type');
        return new fromModels.FileModel(Math.floor(Math.random() * 10000).toString(), file);
      } else {
        this.errorOccurred.emit(
          new fromModels.Message(
            fromEnums.MessageType.WARNING,
            fromEnums.Messages.NOT_VALID_FILE_EXTENSION,
            fromEnums.MessagePositionEnum.TOP,
            this.context
          )
        );
      }
    });

    return fileModels.filter(fileModel => fileModel);
  }


  /**
   * Check if is of a valid type
   * @private
   * @param type the type
   * @returns a boolean
   */
  private isValidType(currentType: string) {
    return (this.acceptedTypes.find(x => x === currentType) !== undefined);
  }
}
